import React from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchContractsReport,
  ContractsReportQuery,
} from "../../../services/partner.service.ts";
import moment from "moment";
import { toCamelCase } from "../../../hooks/toCameCase.tsx";

interface ContractsReportRow {
  partnerId: number;
  name: string;
  branch: {
    id: number | null;
    name: string | null;
  };
  hasContract: boolean;
  hasImssContract: boolean;
  maxValidityDate?: string | null;
}

interface ContractsReportResponse {
  totals: {
    totalPartners: number;
    withContract: number;
    withoutContract: number;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  byPartner: ContractsReportRow[];
}

interface ContractsReportTableProps {
  branchId?: number;
  enabled?: boolean;
}

const ContractsReportTable: React.FC<ContractsReportTableProps> = ({
  branchId,
  enabled = true,
}) => {
  const navigate = useNavigate();
  const [data, setData] = React.useState<ContractsReportResponse | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState<string>("");
  const [searchInput, setSearchInput] = React.useState<string>("");
  const [contractFilter, setContractFilter] = React.useState<
    "all" | "with" | "without"
  >("all");
  const [imssContractFilter, setImssContractFilter] = React.useState<
    "all" | "with" | "without"
  >("all");
  const [page, setPage] = React.useState<number>(1);
  const [pageSize] = React.useState<number>(15);
  const [sortBy, setSortBy] =
    React.useState<ContractsReportQuery["sortBy"]>("name");
  const [sortOrder, setSortOrder] =
    React.useState<ContractsReportQuery["sortOrder"]>("ASC");
  const [showNearExpirationOnly, setShowNearExpirationOnly] = React.useState<boolean>(false);
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  React.useEffect(() => {
    if (!enabled) return;

    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchContractsReport({
          branchId,
          search,
          page,
          limit: pageSize,
          hasContract:
            contractFilter === "all"
              ? undefined
              : contractFilter === "with"
              ? true
              : false,
          hasImssContract:
            imssContractFilter === "all"
              ? undefined
              : imssContractFilter === "with"
              ? true
              : false,
          sortBy,
          sortOrder,
        });
        if (isMounted) {
          setData(response);
        }
      } catch (e) {
        if (isMounted) {
          setError("No se pudo cargar el reporte de contratos.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [
    branchId,
    enabled,
    search,
    page,
    pageSize,
    contractFilter,
    imssContractFilter,
    sortBy,
    sortOrder,
  ]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(value);
    }, 450);
  };

  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const clearFilters = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setSearchInput("");
    setSearch("");
    setContractFilter("all");
    setImssContractFilter("all");
    setShowNearExpirationOnly(false);
    setSortBy("name");
    setSortOrder("ASC");
    setPage(1);
  };

  const isNearExpiration = (validityDate: string | null | undefined): boolean => {
    if (!validityDate) return false;
    const expirationDate = moment(validityDate);
    const today = moment();
    const daysUntilExpiration = expirationDate.diff(today, 'days');
    // Considerar próximo a vencer si está dentro de 30 días
    return daysUntilExpiration >= 0 && daysUntilExpiration <= 30;
  };

  const totalItems = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;
  const rows = data?.byPartner ?? [];
  const filteredRows = showNearExpirationOnly
    ? rows.filter((row) => row.hasImssContract && isNearExpiration(row.maxValidityDate))
    : rows;

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <h3 style={styles.cardTitle}>Detalle de contratos por colaborador</h3>
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchContainer}>
          <input
            type="text"
            placeholder="Buscar colaborador..."
            value={searchInput}
            onChange={handleSearchChange}
            style={styles.searchInput}
          />
        </div>
        <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              backgroundColor: contractFilter === "all" && imssContractFilter === "all" ? "#1E90FF" : "#f3f4f6",
              color: contractFilter === "all" && imssContractFilter === "all" ? "#ffffff" : "#374151",
              borderColor: contractFilter === "all" && imssContractFilter === "all" ? "#1E90FF" : "#d1d5db",
            }}
            onClick={() => {
              setContractFilter("all");
              setImssContractFilter("all");
              setPage(1);
            }}
          >
            Todos
          </button>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              backgroundColor:
                contractFilter === "with"
                  ? "#50B7C0"
                  : "rgba(80, 183, 192, 0.08)",
              color: contractFilter === "with" ? "#ffffff" : "#036672",
              borderColor: "#50B7C0",
            }}
            onClick={() => {
              setContractFilter("with");
              setPage(1);
            }}
          >
            Con contrato
          </button>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              backgroundColor:
                contractFilter === "without"
                  ? "#F7B917"
                  : "rgba(247, 185, 23, 0.10)",
              color: contractFilter === "without" ? "#78350f" : "#92400e",
              borderColor: "#F7B917",
            }}
            onClick={() => {
              setContractFilter("without");
              setPage(1);
            }}
          >
            Sin contrato
          </button>
        </div>
        <div style={styles.filterGroup}>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              backgroundColor:
                imssContractFilter === "with"
                  ? "#50B7C0"
                  : "rgba(80, 183, 192, 0.08)",
              color: imssContractFilter === "with" ? "#ffffff" : "#036672",
              borderColor: "#50B7C0",
            }}
            onClick={() => {
              setImssContractFilter("with");
              setPage(1);
            }}
          >
            Con contrato IMSS
          </button>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              backgroundColor:
                imssContractFilter === "without"
                  ? "#F7B917"
                  : "rgba(247, 185, 23, 0.10)",
              color: imssContractFilter === "without" ? "#78350f" : "#92400e",
              borderColor: "#F7B917",
            }}
            onClick={() => {
              setImssContractFilter("without");
              setPage(1);
            }}
          >
            Sin contrato IMSS
          </button>
        </div>
        <div style={styles.filterGroup}>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              backgroundColor: showNearExpirationOnly ? "rgba(239, 68, 68, 0.12)" : "#f3f4f6",
              color: showNearExpirationOnly ? "#b91c1c" : "#374151",
              borderColor: showNearExpirationOnly ? "rgba(239, 68, 68, 0.4)" : "#d1d5db",
            }}
            onClick={() => {
              setShowNearExpirationOnly((prev) => !prev);
              setPage(1);
            }}
          >
            Próximos a vencer
          </button>
        </div>
        <div style={styles.filterGroup}>
          <button
            type="button"
            style={{
              ...styles.filterButton,
              backgroundColor: "#ef4444",
              color: "#ffffff",
              borderColor: "#dc2626",
            }}
            onClick={clearFilters}
          >
            Limpiar filtros
          </button>
        </div>
        </div>
      </div>

      {error && <div style={styles.errorBody}>{error}</div>}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th
                style={styles.thClickable}
                onClick={() => {
                  setSortBy("name");
                  setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
                }}
              >
                Colaborador
              </th>
              <th style={styles.th}>
                Sucursal
              </th>
              <th style={styles.th}>
                Contrato
              </th>
              <th style={styles.th}>
                Contrato IMSS
              </th>
              <th style={styles.th}>
                Vigencia del Contrato
              </th>
              <th style={styles.th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={styles.emptyCell} colSpan={6}>
                  Cargando contratos...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td style={styles.emptyCell} colSpan={6}>
                  No se encontraron colaboradores para los filtros
                  seleccionados.
                </td>
              </tr>
            ) : (
              filteredRows.map((row, index) => (
                <tr
                  key={row.partnerId}
                  style={{
                    ...styles.tr,
                    ...(index % 2 === 1 ? styles.trAlt : {}),
                  }}
                >
                  <td style={styles.tdName}>{toCamelCase(row.name)}</td>
                  <td style={styles.td}>
                    {row.branch?.name || (
                      <span style={styles.muted}>Sin sucursal</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.contractPill,
                        backgroundColor: row.hasContract
                          ? "rgba(80, 183, 192, 0.10)"
                          : "rgba(247, 185, 23, 0.10)",
                        color: row.hasContract ? "#50B7C0" : "#B45309",
                        borderColor: row.hasContract
                          ? "rgba(80, 183, 192, 0.60)"
                          : "rgba(247, 185, 23, 0.70)",
                      }}
                    >
                      <span
                        style={{
                          ...styles.metaDot,
                          backgroundColor: row.hasContract
                            ? "#50B7C0"
                            : "#F7B917",
                          marginRight: 6,
                        }}
                      />
                      {row.hasContract ? "Con contrato" : "Sin contrato"}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.contractPill,
                        backgroundColor: row.hasImssContract
                          ? "rgba(80, 183, 192, 0.10)"
                          : "rgba(247, 185, 23, 0.10)",
                        color: row.hasImssContract ? "#50B7C0" : "#B45309",
                        borderColor: row.hasImssContract
                          ? "rgba(80, 183, 192, 0.60)"
                          : "rgba(247, 185, 23, 0.70)",
                      }}
                    >
                      <span
                        style={{
                          ...styles.metaDot,
                          backgroundColor: row.hasImssContract
                            ? "#50B7C0"
                            : "#F7B917",
                          marginRight: 6,
                        }}
                      />
                      {row.hasImssContract ? "Con contrato IMSS" : "Sin contrato IMSS"}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {row.maxValidityDate ? (
                      <span
                        style={{
                          ...styles.contractPill,
                          backgroundColor: isNearExpiration(row.maxValidityDate)
                            ? "rgba(239, 68, 68, 0.10)"
                            : "rgba(107, 114, 128, 0.10)",
                          color: isNearExpiration(row.maxValidityDate)
                            ? "#DC2626"
                            : "#6B7280",
                          borderColor: isNearExpiration(row.maxValidityDate)
                            ? "rgba(239, 68, 68, 0.30)"
                            : "rgba(107, 114, 128, 0.30)",
                        }}
                      >
                        {isNearExpiration(row.maxValidityDate) && (
                          <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: "10px", marginRight: "4px" }} />
                        )}
                        {moment(row.maxValidityDate).format("DD/MM/YYYY")}
                      </span>
                    ) : (
                      <span style={styles.muted}>No asignado</span>
                    )}
                  </td>
                  <td style={styles.tdActions}>
                    <button
                      type="button"
                      style={styles.eyeButton}
                      onClick={() =>
                        navigate(`/colaboradores/detalles/${row.partnerId}`)
                      }
                    >
                      <i className="fa-solid fa-eye" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && (
        <div style={styles.pagination}>
          <div style={styles.paginationInfo}>
            Mostrando{" "}
            <strong>
              {(page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, showNearExpirationOnly ? filteredRows.length : totalItems)}
            </strong>{" "}
            de <strong>{showNearExpirationOnly ? filteredRows.length : totalItems}</strong> colaboradores
          </div>
          <div style={styles.paginationControls}>
            <button
              type="button"
              style={{
                ...styles.pageButton,
                ...(page === 1 ? styles.pageButtonDisabled : {}),
              }}
              onClick={() => page > 1 && setPage(page - 1)}
              disabled={page === 1}
            >
              Anterior
            </button>
            <span style={styles.pageIndicator}>
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              style={{
                ...styles.pageButton,
                ...(page === totalPages ? styles.pageButtonDisabled : {}),
              }}
              onClick={() => page < totalPages && setPage(page + 1)}
              disabled={page === totalPages}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    padding: 16,
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    height: "100%",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    padding: "16px 20px",
    margin: "-16px -16px 0 -16px",
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    borderBottom: "1px solid #e5e7eb",
  },
  cardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "#2d3748",
  },
  cardSubtitle: {
    margin: "4px 0 0 0",
    fontSize: 12,
    color: "#6b7280",
  },
  headerMeta: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  metaPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    padding: "4px 8px",
    backgroundColor: "#f9fafb",
  },
  metaDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  metaLabel: {
    fontSize: 11,
    color: "#6b7280",
  },
  metaValue: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111827",
  },
  badge: {
    fontSize: 11,
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
    borderRadius: 999,
    padding: "4px 8px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 8,
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
  searchContainer: {
    flex: 1,
    maxWidth: 400,
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    fontSize: 13,
    outline: "none",
    color: "#111827",
    backgroundColor: "#f9fafb",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)",
    transition: "all 0.2s ease",
  },
  checkboxLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#4b5563",
    cursor: "pointer",
    userSelect: "none",
  },
  checkbox: {
    width: 14,
    height: 14,
    cursor: "pointer",
  },
  filtersContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  filterButton: {
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid transparent",
    backgroundColor: "#f9fafb",
    fontSize: 11,
    color: "#4b5563",
    cursor: "pointer",
  },
  tableWrapper: {
    marginTop: 0,
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    borderTop: "none",
    overflow: "hidden",
    maxHeight: 560,
    overflowY: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    position: "sticky",
    top: 0,
    backgroundColor: "#f9fafb",
    padding: "10px 12px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6b7280",
    borderBottom: "2px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  thClickable: {
    position: "sticky",
    top: 0,
    backgroundColor: "#f9fafb",
    padding: "10px 12px",
    textAlign: "left",
    fontWeight: 600,
    color: "#6b7280",
    borderBottom: "2px solid #e5e7eb",
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  tr: {
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
  },
  trAlt: {
    backgroundColor: "#f9fafb",
  },
  td: {
    padding: "6px 10px",
    color: "#374151",
    fontSize: 12,
    verticalAlign: "middle",
  },
  tdIndex: {
    padding: "6px 10px",
    color: "#6b7280",
    fontSize: 11,
    width: 40,
  },
  tdMono: {
    padding: "6px 10px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas",
    fontSize: 11,
    color: "#4b5563",
    whiteSpace: "nowrap",
  },
  tdName: {
    padding: "6px 10px",
    color: "#111827",
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  tdActions: {
    padding: "6px 8px",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  emptyCell: {
    padding: "16px 10px",
    textAlign: "center",
    fontSize: 12,
    color: "#6b7280",
  },
  muted: {
    color: "#9ca3af",
    fontSize: 11,
  },
  statusPill: {
    display: "inline-flex",
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    fontSize: 11,
    color: "#4b5563",
    backgroundColor: "#f9fafb",
  },
  contractPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 10px",
    borderRadius: 999,
    borderWidth: 1,
    borderStyle: "solid",
    fontSize: 11,
    fontWeight: 500,
  },
  loadingBody: {
    padding: "32px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingSpinner: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    animation: "spin 0.8s linear infinite",
  },
  errorBody: {
    padding: "16px 0",
    fontSize: 12,
    color: "#b91c1c",
  },
  pagination: {
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 12,
    color: "#4b5563",
  },
  paginationInfo: {
    whiteSpace: "nowrap",
  },
  paginationControls: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  pageButton: {
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #d1d5db",
    backgroundColor: "#f9fafb",
    fontSize: 12,
    color: "#374151",
    cursor: "pointer",
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: "default",
  },
  pageIndicator: {
    fontSize: 12,
    color: "#6b7280",
  },
  eyeButton: {
    border: "none",
    borderRadius: 999,
    padding: "4px 10px",
    backgroundColor: "#1E90FF",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: 12,
    boxShadow: "0 1px 4px rgba(30, 144, 255, 0.35)",
  },
  expirationChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    borderWidth: 1,
    borderStyle: "solid",
    fontSize: 10,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
};

export default ContractsReportTable;
